/**
 * How Paiements words money. Never optimistic (ADR-0016 §2.1): a correction
 * reads as done only when the server says `done`; a refund sent to the
 * provider with no answer reads "status unknown, checking", and is never sent
 * again by the screen.
 */
import type {
  ApprovalRequest,
  FeePaymentStatus,
  PaymentActionResponse,
  PaymentAdjustment,
  PaymentAdjustmentStatus,
  PaymentProvider,
} from "@wallyo/contracts/console/payments";
import { ConsoleApiError } from "../../api/errors";
import { describeError } from "../../auth/error-message";
import { t, type MessageId } from "../../i18n";
import { formatMoney, formatMoneyExact, formatNumber } from "../../i18n/format";
import type { WriteFailure } from "../team/team-format";

type Tone = "ok" | "warn" | "bad" | "info" | "mute";

export const PAYMENT_STATUS_TONE: Record<FeePaymentStatus, Tone> = {
  pending: "warn",
  paid: "ok",
  failed: "bad",
  refunded: "info",
  waived: "mute",
};

export const paymentStatusName = (status: FeePaymentStatus) => t(`payments.status.${status}` as const);
export const providerName = (provider: PaymentProvider) => t(`payments.provider.${provider}` as const);

/** A correction's state as the screen shows it: what it says, and its tone. */
export function adjustmentState(adjustment: PaymentAdjustment): { label: string; tone: Tone; hint: string | null } {
  const label = t(`payments.adjustment.status.${adjustment.status}` as const);
  switch (adjustment.status) {
    case "pending":
      return {
        label,
        tone: "warn",
        hint: adjustment.outcomeUnknown ? t("payments.adjustment.outcomeUnknownHint") : null,
      };
    case "awaiting_manual_transfer":
      return { label, tone: "warn", hint: t("payments.adjustment.manualHint") };
    case "awaiting_approval":
      return { label, tone: "info", hint: null };
    case "done":
      return { label, tone: "ok", hint: null };
    case "failed":
      return { label, tone: "bad", hint: adjustment.failureReason };
    case "rejected":
      return { label, tone: "mute", hint: null };
  }
}

const OUTCOME: Record<PaymentAdjustmentStatus, MessageId> = {
  done: "payments.outcome.done",
  pending: "payments.outcome.pending",
  awaiting_manual_transfer: "payments.outcome.awaiting_manual_transfer",
  awaiting_approval: "payments.outcome.awaiting_approval",
  failed: "payments.outcome.failed",
  rejected: "payments.outcome.rejected",
};

/** What the server said a correction did, in words: never "done" before it is. */
export function outcomeMessage(response: PaymentActionResponse): {
  text: string;
  tone: "ok" | "bad" | "warn";
} {
  const { adjustment } = response;
  const amount = formatMoney(adjustment.amount, adjustment.currency);
  const text = t(OUTCOME[adjustment.status], {
    kind: t(`payments.kind.${adjustment.kind}` as const),
    amount,
    failure: adjustment.failureReason ?? "—",
  });
  const tone = adjustment.status === "done" ? "ok" : adjustment.status === "failed" ? "bad" : "warn";
  return { text, tone };
}

/** Which correction a request is waiting for, from its action and its payload. */
function requestKind(request: ApprovalRequest): "refund" | "credit" | "waive" {
  if (request.actionId.startsWith("WAIVE") || request.payload.kind === "waive") return "waive";
  if (request.actionId.startsWith("ISSUE_CREDIT") || request.payload.kind === "credit") return "credit";
  return "refund";
}

/**
 * What an approval request asks for, read from its payload, and what the
 * requester held when they asked (ADR-0019 §6): their limit in the request's
 * currency, or that they don't hold the permission at all.
 */
export function approvalSummary(request: ApprovalRequest): {
  kind: "refund" | "credit" | "waive";
  amount: string | null;
  /** The fee it corrects; null for a credit, which belongs to the pro. */
  platformFeePaymentId: string | null;
  /** The pro a credit goes to, when the request names one. */
  workerId: string | null;
  permission: string;
  /** What to say about the requester's own limit. */
  held: { kind: "none" } | { kind: "noLimit" } | { kind: "limit"; amount: string };
} {
  const payload = request.payload;
  const kind = requestKind(request);
  let currency: string | null = null;
  if (typeof payload.currency === "string") currency = payload.currency;
  let amount: string | null = null;
  if (typeof payload.amount === "number" && currency !== null) amount = formatMoney(payload.amount, currency);
  if (typeof payload.amount === "number" && currency === null) amount = formatNumber(payload.amount);

  let platformFeePaymentId: string | null = null;
  if (typeof payload.platformFeePaymentId === "string") platformFeePaymentId = payload.platformFeePaymentId;
  if (platformFeePaymentId === null && request.targetType === "platform_fee_payment") {
    platformFeePaymentId = request.targetId;
  }
  let workerId: string | null = null;
  if (request.targetType === "pro_profile") workerId = request.targetId;

  let held: { kind: "none" } | { kind: "noLimit" } | { kind: "limit"; amount: string } = { kind: "none" };
  if (request.heldAtRequest && request.limitAtRequest === null) held = { kind: "noLimit" };
  if (request.limitAtRequest !== null) {
    // Their ceiling, exactly: rounded up it would overstate what they may do alone.
    let ceiling = formatNumber(request.limitAtRequest);
    if (currency !== null) ceiling = formatMoneyExact(request.limitAtRequest, currency);
    held = { kind: "limit", amount: ceiling };
  }
  return {
    kind,
    amount,
    platformFeePaymentId,
    workerId,
    permission: request.permissionId,
    held,
  };
}

export type PaymentFailureContext = "general" | "approval";

/**
 * The server's reasons a money write is refused for (ConsoleErrorReason), in
 * the staff member's words. `stale`: the payment moved on, so offer a reload.
 */
const REFUSALS: Readonly<Record<string, { message: MessageId; stale: boolean }>> = {
  stale_version: { message: "payments.error.stale", stale: true },
  not_refundable: { message: "payments.refused.notRefundable", stale: true },
  nothing_to_refund: { message: "payments.refused.nothingToRefund", stale: true },
  refund_too_large: { message: "payments.refused.refundTooLarge", stale: false },
  already_awaiting_approval: { message: "payments.refused.alreadyAwaitingApproval", stale: true },
  not_markable: { message: "payments.refused.notMarkable", stale: true },
  reference_in_use: { message: "payments.refused.referenceInUse", stale: false },
  reference_required: { message: "payments.refused.referenceRequired", stale: false },
  already_settled: { message: "payments.refused.alreadySettled", stale: true },
  already_decided: { message: "payments.refused.alreadyDecided", stale: true },
  own_request: { message: "payments.refused.ownRequest", stale: false },
  // The approver doesn't hold the permission, or not for this amount (ADR-0019 §6).
  approver_not_permitted: {
    message: "payments.refused.approverNotPermitted",
    stale: false,
  },
  payment_changed: { message: "payments.refused.paymentChanged", stale: true },
  currency_mismatch: { message: "payments.refused.currencyMismatch", stale: false },
  fee_of_another_pro: { message: "payments.refused.feeOfAnotherPro", stale: false },
  invalid_amount: { message: "payments.refused.invalidAmount", stale: false },
};

/**
 * A refused money write (ADR-0016 §2.5), from the server's `reason` when this
 * knows it; otherwise translated by its code. Never the server's English
 * text; always the request ID. A lost answer says the correction may have
 * been recorded: "try again" reuses the same key, so it can't run twice.
 */
export function describePaymentFailure(error: unknown, context: PaymentFailureContext = "general"): WriteFailure {
  const { message, reference } = describeError(error);
  if (!(error instanceof ConsoleApiError)) return { message, reference, stale: false };
  const refusal = error.reason ? REFUSALS[error.reason] : undefined;
  if (refusal) return { message: t(refusal.message), reference, stale: refusal.stale };
  switch (error.code) {
    case "stale_version":
      return { message: t("payments.error.stale"), reference, stale: true };
    case "feature_not_enabled":
      return {
        message: t(context === "approval" ? "payments.error.approvalRefused" : "payments.error.refused"),
        reference,
        stale: false,
      };
    case "validation_error":
      return { message: t("payments.error.invalid"), reference, stale: true };
    case "network_error":
      return error.outcomeUnknown
        ? { message: t("payments.error.outcomeUnknown"), reference, stale: false }
        : { message, reference, stale: false };
    default:
      return { message, reference, stale: false };
  }
}
